import React = require("react");
import { PublicAmorgUser, Activity, ActivityParticipantRole, assertNever, ActivityParticipantData } from "../../../amorg-common";
import { Button } from "../../../designSystem/button/Button";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { Modal } from "../../../designSystem/Modal";
import { ParticipantEditor } from "./ParticipantEditor";
import { ChipPanel, InfoChip } from "../../../designSystem/Chip";
import { Span12, Span14 } from "../../../designSystem/typography/Body";
import { UserAvatarCard } from "../../../designSystem/avatar/AvatarCard";
import { CountAvatar } from "../../../designSystem/avatar/CountAvatar";

type MeetingParticipantListProps = {
    users: PublicAmorgUser[];
    activity: Activity;
    onParticipantsUpdated (): void;
};
export function MeetingParticipantList (props: MeetingParticipantListProps): JSX.Element
{
    const [modal, setModal] = React.useState<{ role: ActivityParticipantRole; } | null>(null);

    function handleClickEditParticipants (role: ActivityParticipantRole)
    {
        setModal({ role });
    }

    function handleParticipantsUpdated ()
    {
        props.onParticipantsUpdated();
        if (modal === null)
        {
            throw new Error('Modal role is null when updating participants');
        }
        else if (modal.role === 'facilitator' || modal.role === 'minuteTaker')
        {
            // Close the modal - only one can be selected for these roles, so the user is done after selecting one
            setModal(null);
        }
        else if (modal.role === 'participant')
        {
            // Keep the modal open - many can be selected for this role, so the user may want to add more
        }
        else
        {
            assertNever(modal.role);
        }
    }

    return (
        <>
            <Flex gap={32} justifyContent="stretch" flexWrap="wrap">
                <div style={{flex:'1 1 0'}}>
                    <SinglePersonRolePanel
                        role="facilitator"
                        roleText="Facilitator / mødeleder"
                        user={props.activity.participants.find(p => p.activityRole === 'facilitator')!}
                        onClick={() => handleClickEditParticipants('facilitator')}
                    />
                </div>
                <div style={{flex:'1 1 0'}}>
                    <SinglePersonRolePanel
                        role="minuteTaker"
                        roleText="Referent"
                        user={props.activity.participants.find(p => p.activityRole === 'minuteTaker')!}
                        onClick={() => handleClickEditParticipants('minuteTaker')}
                    />
                </div>
                <div style={{flex:'2 2 0'}}>
                    <MeetingRolePanelHeader roleText="Deltagere" count={props.activity.participants.filter(p => p.activityRole === 'participant').length} onClick={() => handleClickEditParticipants('participant')} />
                    <Flex alignItems="center" style={{minHeight: '50px'}}>
                        <ParticipantPillList participants={props.activity.participants} />
                    </Flex>
                </div>
            </Flex>
            <Flex flexDirection="row" gap={16}>


            <Modal
                title={"Rediger deltagere"}
                content={
                    <ParticipantEditor
                        activity={props.activity}
                        defaultAddRole={modal?.role ?? 'participant'}
                        onParticipantsUpdated={handleParticipantsUpdated}
                        onCancel={() => setModal(null)} />
                }
                showModal={modal !== null}
                onCancel={() => setModal(null)} />

            </Flex>
        </>
    );
}

function MeetingRolePanelHeader (props: {
    roleText: string,
    count?: number,
    onClick: () => void,
}): JSX.Element
{
    return (
        <Flex alignItems="center" justifyContent="flex-start">
            <Flex alignItems="center" gap={4}>
                <Span12 color="gray" fontWeight={500} textTransform="uppercase">{props.roleText}</Span12>
                {props.count !== undefined && 
                    <CountAvatar count={props.count} size="20px" />
                }
            </Flex>
            <Button variant="text" size="32px" onClick={props.onClick} icon="interface_actions_edit" />
        </Flex>
    );
}

function SinglePersonRolePanel(props:{
    role: ActivityParticipantRole,
    roleText: string,
    user?:ActivityParticipantData,
    onClick: () => void,
}): JSX.Element
{
    return (
        <div>
            <MeetingRolePanelHeader
                roleText={props.roleText}
                onClick={props.onClick}
            />
                <UserAvatarCard
                    noBorder
                    user={
                        props.user
                        ?   {
                                displayName: props.user?.name || '',
                                initials: props.user?.initials || '',
                                role: props.user?.role || '',
                        }
                        : undefined
                    }
                />
        </div>
    );
}

function ParticipantPillList ({ participants }: { participants: ActivityParticipantData[]; }): JSX.Element
{
    const sortedParticipants = [...participants]?.sort((a,b) => a.name.localeCompare(b.name)).filter(p => p.activityRole === 'participant')
    
    return (
        <>
            {participants.length === 0 
                ?   <Span14 italic>Ingen deltagere</Span14>
                :   <ChipPanel>
                        {sortedParticipants.map(p =>
                            p.type.value === 'external'
                                ?   <InfoChip key={p.id} variant="outlined">
                                        <span>{p.name} (ekstern)</span>
                                    </InfoChip>
                                :   <InfoChip key={p.id} colorHash={p.name}>
                                        <span>{p.name}</span>
                                    </InfoChip>
                        )}
                    </ChipPanel>
            }
        </>
    );
}